import {
  DeleteOutline,
  Edit,
  SearchOutlined,
  AddCircleOutline,
  CategoryOutlined,
  RouteOutlined,
  FolderOutlined,
  TrendingUp,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useEffect, useState } from 'react';

function Category() {
  const server_url = process.env.REACT_APP_API_URI;

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCategories: 0,
    activeCategories: 0,
  });

  const getAllCategories = async (page = 1, limitValue = limit) => {
    try {
      setLoading(true);
      const url = `${server_url}/context/get-all-category?page=${page}&limit=${limitValue}`;
      const response = await axios.get(url);
      const categoriesData = response.data.data;
      setCategories(categoriesData);
      setFilteredCategories(categoriesData);
      setTotalPages(response.data.totalPages || 1);

      // Calculate stats
      setStats({
        totalCategories: categoriesData.length,
        activeCategories: categoriesData.length,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllCategories(currentPage, limit);
  }, [server_url, currentPage, limit]);

  useEffect(() => {
    let filtered = [...categories];

    if (searchTerm) {
      filtered = filtered.filter(
        (cat) =>
          cat?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat?.route?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          cat?.id?.toString().includes(searchTerm),
      );
    }

    setFilteredCategories(filtered);
  }, [searchTerm, categories]);

  const handleCategoryDelete = async (catId) => {
    try {
      if (window.confirm('Are you sure you want to delete this category?')) {
        const response = await axios.post(
          `${server_url}/category/delete-category`,
          { catId },
        );
        alert(response.data.msg);
        if (response.data.status) getAllCategories(currentPage, limit);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Category Management
        </h1>
        <p className="text-gray-600">
          Manage rating categories and their configurations
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Total Categories
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalCategories}
              </h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <CategoryOutlined className="text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Active Categories
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.activeCategories}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FolderOutlined className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Add Button */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, route, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <Link to="/admin/newCategory" className="w-full">
            <button className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-md hover:shadow-lg">
              <AddCircleOutline />
              Add New Category
            </button>
          </Link>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="font-medium text-gray-700">Show:</label>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[10, 20, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="text-gray-600">entries</span>
        </div>

        <div className="text-gray-600">
          Showing{' '}
          {filteredCategories.length > 0 ? (currentPage - 1) * limit + 1 : 0} to{' '}
          {Math.min(currentPage * limit, filteredCategories.length)} of{' '}
          {filteredCategories.length}{' '}
          {filteredCategories.length !== categories.length &&
            `(filtered from ${categories.length})`}
        </div>
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                <tr>
                  <th className="py-4 px-6 text-left font-semibold">ID</th>
                  <th className="py-4 px-6 text-left font-semibold">
                    Category
                  </th>
                  <th className="py-4 px-6 text-left font-semibold">Route</th>
                  <th className="py-4 px-6 text-left font-semibold">
                    Taxonomy
                  </th>
                  <th className="py-4 px-6 text-center font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCategories.length > 0 ? (
                  filteredCategories.map((category) => (
                    <tr
                      key={category._id}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="py-4 px-6">
                        <span className="text-gray-600 font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                          {category.id}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {category.title?.charAt(0).toUpperCase() || 'C'}
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800">
                              {category.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              Rating Category
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <RouteOutlined
                            className="text-gray-400"
                            fontSize="small"
                          />
                          <span className="text-gray-700 font-mono text-sm">
                            {category.route}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex flex-col gap-1">
                          {category.vertical && (
                            <span className="inline-block px-2 py-1 text-xs font-semibold text-purple-700 bg-purple-100 rounded-full w-max">
                              {category.vertical}
                            </span>
                          )}
                          {(category.categoryName || category.subCategory) && (
                            <div className="text-xs text-gray-500">
                              {category.categoryName && (
                                <span>{category.categoryName}</span>
                              )}
                              {category.categoryName &&
                                category.subCategory && (
                                  <span className="mx-1">&gt;</span>
                                )}
                              {category.subCategory && (
                                <span>{category.subCategory}</span>
                              )}
                            </div>
                          )}
                          {!category.vertical &&
                            !category.categoryName &&
                            !category.subCategory && (
                              <span className="text-xs text-gray-400 italic">
                                No taxonomy data
                              </span>
                            )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to="/admin/update-category/"
                            state={{ catId: category._id }}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Edit Category"
                          >
                            <Edit fontSize="small" />
                          </Link>
                          <button
                            onClick={() => handleCategoryDelete(category._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Category"
                          >
                            <DeleteOutline fontSize="small" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <CategoryOutlined className="text-gray-400 text-5xl" />
                        <p className="text-gray-500 text-lg font-medium">
                          {searchTerm
                            ? 'No categories match your search'
                            : 'No categories found'}
                        </p>
                        <p className="text-gray-400 text-sm">
                          {searchTerm
                            ? 'Try adjusting your search term'
                            : 'Click "Add New Category" to create one'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Previous
          </button>

          <div className="flex items-center gap-2">
            {[...Array(Math.min(totalPages, 5))].map((_, index) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = index + 1;
              } else if (currentPage <= 3) {
                pageNumber = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + index;
              } else {
                pageNumber = currentPage - 2 + index;
              }

              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="w-full md:w-auto px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Category;
